/**
 * Configuration des agents GANDAS
 * 
 * Définit les agents visibles dans l'application.
 * Les URLs de webhooks sont volontairement maintenues côté serveur dans
 * server-agent-config.ts afin de ne jamais les inclure dans le bundle client.
 */

/**
 * Famille d'agent, telle que présentée à l'utilisateur.
 * - 'b2b'    : met en relation avec un commerce partenaire (un établissement courant
 *              existe, on peut en changer). Ces agents partagent la gateway resto-web.
 * - 'gandas' : agents propres à Gandas, qui répondent directement.
 */
export type AgentGroup = 'b2b' | 'gandas';

export const AGENT_GROUP_LABELS: Record<AgentGroup, string> = {
  b2b: 'Agents B2B',
  gandas: 'Agents Gandas',
};

export type AgentChannel = 'web' | 'telegram' | 'whatsapp';

export const AGENT_CHANNEL_LABELS: Record<AgentChannel, string> = {
  web: 'Web',
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
};

export interface Agent {
  id: string;
  name: string;
  specialty: string;
  description: string;
  avatar?: string;
  color: string;
  group: AgentGroup;
  status: 'active' | 'coming_soon';
  capabilities: string[];
  /** Canaux où l'agent est joignable (coming_soon : canaux prévus). */
  channels: AgentChannel[];
  /**
   * Bot Telegram PROPRE à l'agent, quand il en a un.
   * Absent = on retombe sur le bot Gandas unifié. L'absence EST l'information :
   * plusieurs agents déclarent le canal `telegram` sans avoir de bot à eux.
   */
  telegramBot?: string;
  /**
   * Numéro WhatsApp PROPRE à l'agent (format wa.me : indicatif + numéro, chiffres seuls).
   * Absent = pas de bouton « Ouvrir WhatsApp » — contrairement à Telegram, aucun repli :
   * ouvrir un mauvais numéro serait pire que ne rien proposer.
   */
  whatsappNumber?: string;
  /** Prompts d'exemple affichés sur la carte de la grille. */
  examples: string[];
}

export const agents: Agent[] = [
  {
    id: 'gandas',
    name: 'Gandas',
    specialty: 'Gestion Automatisée',
    description: 'Votre bras droit numérique pour devis, factures et gestion du quotidien, pendant que vous développez votre business.',
    avatar: '/agents/gandas.webp',
    color: 'bg-gradient-to-br from-indigo-500 to-blue-600',
    group: 'gandas',
    status: 'active',
    capabilities: [
      'Devis et factures en 2 clics',
      'Relances clients automatiques',
      'Suivi des paiements',
      'Rapports de vente simplifiés'
    ],
    channels: ['web', 'telegram', 'whatsapp'],
    telegramBot: 'agents_gandas_bot',
    whatsappNumber: '22554110199',
    examples: ['Fais-moi un devis pour 10 sacs de ciment', 'Relance les clients qui n\'ont pas payé'],
  },
  {
    id: 'resto',
    name: 'Resto',
    specialty: 'Restaurants & Commande',
    description: 'Trouvez un restaurant près de vous, consultez le menu et passez commande auprès de nos restaurants partenaires.',
    avatar: '/agents/resto.webp',
    color: 'bg-gradient-to-br from-orange-500 to-red-600',
    group: 'b2b',
    status: 'active',
    capabilities: [
      'Restaurants partenaires à proximité',
      'Consultation du menu',
      'Commande et livraison',
      'Réservation de table',
      'Suivi de commande'
    ],
    channels: ['web', 'telegram', 'whatsapp'],
    telegramBot: 'agent_resto_bot',
    whatsappNumber: '2250105499071',
    examples: ['Trouve un restaurant près de Cocody', 'Montre-moi le menu du jour'],
  },
  {
    id: 'fouka',
    name: 'Fouka',
    specialty: 'Boutiques & Shopping',
    description: 'Fouka vous aide à découvrir des articles (bijoux, bien-être, vêtements…) dans nos boutiques partenaires et à commander.',
    avatar: '/agents/fouka.webp',
    color: 'bg-gradient-to-br from-amber-500 to-orange-600',
    group: 'b2b',
    status: 'active',
    capabilities: [
      'Boutiques partenaires',
      'Catalogue d\'articles',
      'Photos des produits',
      'Commande et livraison',
      'Recherche d\'articles'
    ],
    channels: ['web', 'telegram', 'whatsapp'],
    telegramBot: 'agent_fouka_bot',
    whatsappNumber: '22584550881',
    examples: ['Je cherche un bijou pour un cadeau', 'Montre-moi les nouveautés bien-être'],
  },
  {
    id: 'ahouli',
    name: 'Ahouli',
    specialty: 'Logement & Immobilier',
    description: 'Ahouli vous aide à trouver un logement ou des locaux — résidence, hôtel, appartement, boutique ou bureau — et à réserver chez nos partenaires.',
    avatar: '/agents/ahouli.webp',
    color: 'bg-gradient-to-br from-pink-500 to-rose-600',
    group: 'b2b',
    status: 'active',
    capabilities: [
      'Biens à vendre ou à louer',
      'Résidences et hôtels partenaires',
      'Réservation de chambre',
      'Disponibilités par dates',
      'Photos des chambres',
      'Paiement à l\'arrivée'
    ],
    channels: ['web', 'telegram', 'whatsapp'],
    telegramBot: 'agent_ahouli_bot',
    whatsappNumber: '22585767992',
    examples: ['Trouve-moi un studio à louer à Abidjan', 'Une chambre pour ce week-end'],
  },
  {
    id: 'viva',
    name: 'Viva',
    specialty: 'Formation & Compétences',
    description: "Votre compagnon d'apprentissage pour les formations « L'IA au quotidien » : comprend le contenu de vos cours et vous aide à appliquer l'IA dans votre métier.",
    avatar: '/agents/viva.webp',
    color: 'bg-gradient-to-br from-green-500 to-emerald-600',
    group: 'gandas',
    status: 'active',
    capabilities: [
      'Explique le contenu des modules de formation',
      'Applique la méthode PRECIS à vos prompts',
      'Exemples adaptés à votre métier',
      'Questions/réponses sur les cours'
    ],
    channels: ['web', 'telegram', 'whatsapp'],
    telegramBot: 'agent_viva_bot',
    whatsappNumber: '2250105499070',
    examples: ['Explique-moi la méthode PRECIS', 'Aide-moi à appliquer l\'IA dans mon métier'],
  },
  {
    id: 'vichenzo',
    name: 'Vichenzo',
    specialty: 'Assistant Multi-Services',
    description: 'Assistant créatif et technique : idées, rédaction et contenus. Génération de visuels, vidéos, musique et documents bientôt disponible.',
    avatar: '/agents/vichenzo.webp',
    color: 'bg-gradient-to-br from-teal-500 to-cyan-600',
    group: 'gandas',
    status: 'active',
    capabilities: [
      'Assistance créative et technique',
      'Rédaction et brainstorming',
      'Génération de visuels',
      'Génération de vidéos et musique',
      'Création de documents'
    ],
    channels: ['web', 'telegram', 'whatsapp'],
    telegramBot: 'vichenzo_dev_bot',
    whatsappNumber: '22576782410',
    examples: ['Rédige un post pour ma boutique', 'Donne-moi des idées de slogan'],
  },
  {
    id: 'bara',
    name: 'Bara',
    specialty: 'Emploi & Recrutement',
    description: 'Bara trouve des offres adaptées au profil des candidats et aide les employeurs à repérer des candidatures consentantes.',
    avatar: '/agents/bara.webp',
    color: 'bg-gradient-to-br from-sky-500 to-cyan-600',
    group: 'gandas',
    status: 'active',
    capabilities: [
      'Aide à la recherche d\'emploi',
      'Assistance recrutement',
      'Optimisation CV',
      'Préparation entretiens',
      'Stratégies carrière'
    ],
    channels: ['web'],
    examples: ['Trouve des offres pour un profil comptable', 'Améliore mon CV'],
  },
  {
    id: 'diji',
    name: 'Diji',
    specialty: 'Marketing & Visibilité',
    description: 'Faites connaître votre business sur les réseaux. Diji gère votre présence digitale pour attirer plus de clients.',
    color: 'bg-gradient-to-br from-violet-500 to-purple-600',
    group: 'gandas',
    status: 'coming_soon',
    capabilities: [
      'Stratégie de contenu marketing',
      'Création de contenus optimisés SEO',
      'Campagnes multi-canaux',
      'Analyse de performance'
    ],
    channels: ['web', 'telegram'],
    examples: ['Fais connaître mon salon de coiffure', 'Prépare un planning de posts'],
  },
  {
    id: 'medic',
    name: 'Medic',
    specialty: 'Santé & Urgences',
    description: 'Medic localise les hôpitaux, cliniques et pharmacies de garde, et prépare vos demandes de lunettes Optic Premium.',
    avatar: '/agents/medic.webp',
    color: 'bg-gradient-to-br from-blue-500 to-purple-600',
    group: 'gandas',
    status: 'active',
    capabilities: [
      'Conseils santé',
      'Assistance premiers secours',
      'Pharmacies de garde',
      'Hôpitaux à proximité',
      'Gestion des urgences'
    ],
    channels: ['web'],
    examples: ['Quelle pharmacie de garde près de Yopougon ?', 'L\'hôpital le plus proche'],
  },
  {
    id: 'rdv',
    name: 'RDV',
    specialty: 'Réservations & Rappels',
    description: 'Prise de rendez-vous automatique pour votre activité — salons, cabinets, ateliers. RDV confirme, rappelle et replanifie pour vous.',
    color: 'bg-gradient-to-br from-fuchsia-500 to-pink-600',
    group: 'gandas',
    status: 'coming_soon',
    capabilities: [
      'Prise de rendez-vous 24h/24',
      'Rappels automatiques aux clients',
      'Replanification en un message',
      'Agenda multi-métiers'
    ],
    channels: ['web', 'telegram', 'whatsapp'],
    examples: ['Prends un rendez-vous coiffure demain à 15h', 'Rappelle mes clients de la semaine'],
  },
  {
    id: 'relances',
    name: 'Relances',
    specialty: 'Factures & Paiements',
    description: 'Relance vos factures impayées sans y penser : suivi des échéances, messages de relance progressifs et confirmation d\'encaissement.',
    color: 'bg-gradient-to-br from-yellow-500 to-amber-600',
    group: 'gandas',
    status: 'coming_soon',
    capabilities: [
      'Suivi des échéances',
      'Relances progressives automatiques',
      'Confirmation de paiement',
      'Récapitulatif hebdomadaire'
    ],
    channels: ['web', 'whatsapp'],
    examples: ['Relance les factures en retard de plus de 7 jours', 'Où en est la facture de Kouassi ?'],
  },
  {
    id: 'vocal',
    name: 'Vocal',
    specialty: 'Réception d\'appels',
    description: 'Un standard téléphonique intelligent qui décroche pour votre business, répond aux questions courantes et prend les messages.',
    color: 'bg-gradient-to-br from-lime-500 to-green-600',
    group: 'gandas',
    status: 'coming_soon',
    capabilities: [
      'Réponse aux appels entrants',
      'Questions fréquentes au téléphone',
      'Prise de messages et transfert',
      'Disponible 24h/24'
    ],
    channels: ['web'],
    examples: ['Configure mon accueil téléphonique', 'Quels appels ai-je manqués aujourd\'hui ?'],
  }
];

/**
 * Récupérer un agent par son ID
 */
export function getAgentById(id: string): Agent | undefined {
  return agents.find(agent => agent.id === id);
}

/**
 * Récupérer les agents actifs
 */
export function getActiveAgents(): Agent[] {
  return agents.filter(agent => agent.status === 'active');
}

/**
 * Récupérer les agents actifs regroupés par famille, dans l'ordre d'affichage
 * de la sidebar. Les familles vides sont omises.
 */
export function getActiveAgentsByGroup(): Array<{ group: AgentGroup; label: string; agents: Agent[] }> {
  const order: AgentGroup[] = ['b2b', 'gandas'];
  return order
    .map(group => ({
      group,
      label: AGENT_GROUP_LABELS[group],
      agents: getActiveAgents().filter(agent => agent.group === group),
    }))
    .filter(section => section.agents.length > 0);
}

/**
 * Récupérer les agents à venir
 */
export function getComingSoonAgents(): Agent[] {
  return agents.filter(agent => agent.status === 'coming_soon');
}

/**
 * Récupérer l'agent par défaut (Gandas)
 */
export function getDefaultAgent(): Agent {
  return agents[0]; // Gandas
}

/**
 * Vérifier si un agent est disponible
 */
export function isAgentAvailable(agentId: string): boolean {
  const agent = getAgentById(agentId);
  return agent?.status === 'active';
}

// Stats des agents
export const agentStats = {
  total: agents.length,
  active: getActiveAgents().length,
  comingSoon: getComingSoonAgents().length
};

export default agents;

/**
 * Bot Telegram à utiliser pour un agent donné.
 * SEUL point de décision : l'app avait une variable unique, ce qui envoyait tout le monde
 * vers @agents_gandas_bot — y compris depuis le chat de Fouka, qui a son propre bot
 * depuis le 16/08. Ajouter un bot = renseigner `telegramBot` sur l'agent, rien d'autre.
 */
export function telegramBotFor(agentId: string): string {
  return agents.find(a => a.id === agentId)?.telegramBot
    || process.env.NEXT_PUBLIC_TELEGRAM_BOT_USERNAME
    || 'agents_gandas_bot';
}

/**
 * Numéro WhatsApp (format wa.me) d'un agent, ou null s'il n'en a pas.
 * Pas de repli global, contrairement à telegramBotFor : le bouton « Ouvrir WhatsApp »
 * est simplement masqué pour les agents sans numéro.
 */
export function whatsappNumberFor(agentId: string): string | null {
  return agents.find(a => a.id === agentId)?.whatsappNumber ?? null;
}

/**
 * Liens directs vers les canaux PROPRES à un agent, pour les raccourcis du tiroir mobile.
 * Contrairement à telegramBotFor, aucun repli sur le bot partagé : un agent « web uniquement » (Bara…)
 * ne doit pas envoyer l'utilisateur vers le bot d'un autre. Un canal n'est exposé que si l'agent le déclare
 * dans `channels` ET possède l'identifiant correspondant.
 */
export function getAgentChannelLinks(agentId: string): { telegram: string | null; whatsapp: string | null } {
  const agent = agents.find(a => a.id === agentId);
  if (!agent) return { telegram: null, whatsapp: null };
  return {
    telegram: agent.channels.includes('telegram') && agent.telegramBot ? `https://t.me/${agent.telegramBot}` : null,
    whatsapp: agent.channels.includes('whatsapp') && agent.whatsappNumber ? `https://wa.me/${agent.whatsappNumber}` : null,
  };
}
